import React, { useCallback, useEffect, useRef, useState } from 'react';
import { taches, messageErreur } from '../services/api';
import { Avatar, Chargement, Erreur } from './communs';
import ChampMention from './ChampMention';
import { ilYA, horodatage, nomComplet } from '../utils/format';

// Met en valeur « @Prénom Nom » pour chaque profil réellement mentionné.
function TexteAvecMentions({ texte, mentions }) {
  const noms = mentions.map((m) => `@${nomComplet(m)}`).filter((n) => n.length > 1);
  if (!noms.length) return texte;
  const motif = new RegExp(`(${noms.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  return texte.split(motif).map((morceau, i) => (noms.includes(morceau) ? <span key={i} className="mention">{morceau}</span> : morceau));
}

/**
 * Mur d'activité d'une tâche : messages et événements dans un même fil,
 * rafraîchi toutes les 20 s tant qu'il est ouvert.
 */
export default function MurActivite({ tacheId, moi, utilisateurs, majTache }) {
  const [messages, setMessages] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const fin = useRef(null);
  const nbPrecedent = useRef(0);

  const charger = useCallback(() => taches.messages(tacheId)
    .then((m) => { setMessages(m); setErreur(null); })
    .catch((e) => setErreur(messageErreur(e))), [tacheId]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 20000);
    return () => clearInterval(t);
  }, [charger, majTache]);

  // Descend au dernier message quand il en arrive un nouveau.
  useEffect(() => {
    if (messages && messages.length > nbPrecedent.current) fin.current?.scrollIntoView({ block: 'nearest' });
    nbPrecedent.current = messages?.length || 0;
  }, [messages]);

  async function envoyer(texte, mentions) {
    setEnvoi(true);
    try {
      await taches.ecrire(tacheId, texte, mentions);
      await charger();
      return true;
    } catch (e) {
      setErreur(messageErreur(e));
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  async function effacer(m) {
    if (!window.confirm('Supprimer ce message ?')) return;
    try { await taches.effacerMessage(tacheId, m.id); charger(); } catch (e) { setErreur(messageErreur(e)); }
  }

  return (
    <div className="panneau-section">
      <h3 className="carte-titre">Mur d’activité</h3>
      {erreur && <Erreur onReessayer={charger}>{erreur}</Erreur>}
      {!messages && !erreur && <Chargement />}
      {messages && (
        <div className="mur" aria-live="polite">
          {messages.length === 0 && <div className="faible" style={{ fontSize: 13 }}>Aucun échange pour l’instant. Lancez la discussion ci-dessous.</div>}
          {messages.map((m) => (m.type === 'evenement' ? (
            <div key={m.id} className="mur-evenement" title={horodatage(m.created_at)}>
              <b>{nomComplet(m, 'auteur_') || 'Profil supprimé'}</b> {m.texte} · {ilYA(m.created_at)}
            </div>
          ) : (
            <div key={m.id} className={`mur-message ${m.auteur_id === moi.id ? 'moi' : ''}`}>
              <Avatar prenom={m.auteur_prenom} nom={m.auteur_nom} />
              <div className="mur-bulle">
                <div className="mur-auteur">
                  <b>{nomComplet(m, 'auteur_') || 'Profil supprimé'}</b>
                  <span className="faible" title={horodatage(m.created_at)}>{ilYA(m.created_at)}</span>
                  {(m.auteur_id === moi.id || moi.role === 'admin') && (
                    <button type="button" className="mur-effacer" onClick={() => effacer(m)}>Supprimer</button>
                  )}
                </div>
                <div className="mur-texte"><TexteAvecMentions texte={m.texte} mentions={m.mentions} /></div>
              </div>
            </div>
          )))}
          <div ref={fin} />
        </div>
      )}
      <ChampMention utilisateurs={utilisateurs} onEnvoyer={envoyer} envoi={envoi} />
    </div>
  );
}
